import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateEmailDomains, useUpdateEmailAddress } from 'api/company/hooks';
import { EmailAddress } from 'api/company/requests';

type ModalProps = {
  address: EmailAddress;
};

export const useEditSenderNameModal = () => useModal(EditSenderNameModal);

type FormValues = {
  fromName: string;
};

const Form = createForm<FormValues>({ space: 0 });

const EditSenderNameModal = ({ address }: ModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const updateMutation = useUpdateEmailAddress();
  const { invalidate } = useInvalidateEmailDomains();

  const handleSubmit = async (values: FormValues) => {
    await updateMutation.mutateAsync({
      shortId: address.shortId,
      data: { fromName: values.fromName },
    });
    await invalidate();
    hideModal();
  };
  const isDirty = formStore.useIsDirty();

  return (
    <Modal>
      <Form onSubmit={handleSubmit} store={formStore}>
        <Modal.SimpleHeader title={t('editSenderName')} />
        <Modal.Content>
          <Stack space={6}>
            <Stack space={2}>
              <Text text={t('emailAddress')} font="semibold14" />
              <Text text={address.fromAddress} font="regular14" color="textSecondary" />
            </Stack>

            <Form.TextField
              id="fromName"
              label={t('senderName')}
              placeholder={t('senderNamePlaceholder')}
              validators={['required']}
              defaultValue={address.fromName}
              maxChars={100}
              helpText={t('maxLength')}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('save')}
              variant="primary-blue"
              isDisabled={updateMutation.isLoading || !isDirty}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    editSenderName: 'Edit sender name',
    emailAddress: 'Email address',
    senderName: 'Sender name',
    senderNamePlaceholder: 'Acme inc',
    save: 'Save',
    maxLength: 'Maximum length is 100 characters',
  },
};
